import React from "react";
import { connect } from "react-redux";
import { Col, Row, Form } from "react-bootstrap";
import PropTypes from "prop-types";
import Select from "react-select";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./email.css";

import { loading } from "../../../actions/page";
import { get as getConfigurations } from "../../../actions/email.configuration";
import { get as getPersonSettings, save as savePersonSettings } from "../../../actions/person.settings";
import { send as sendAlert } from "../../../actions/alert";

class ProfileEmailSettings extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            configurations: [],
            settings: {},
            loadedFor: null,
        };
    }

    componentDidMount = () => {
        setTimeout(this.loadData, 30);
    };

    componentDidUpdate(prevProps) {
        if (prevProps.owner !== this.props.owner) {
            this.loadData();
        }
    }

    loadData = async () => {
        if (!this.props.owner) return;

        this.props.loading(true);
        try {
            const [configurations, personalSettings] = await Promise.all([
                this.props.getConfigurations(),
                this.props.getPersonSettings({ personUuid: this.props.owner }),
            ]);
            const settings = personalSettings?.settings || {};

            this.setState({
                configurations: Array.isArray(configurations) ? configurations : [],
                settings: {
                    ...settings,
                    EMAIL_DISPLAY: settings.EMAIL_DISPLAY || this.props.displayName || "",
                    EMAIL_SENDER: settings.EMAIL_SENDER || this.props.email || "",
                    EMAIL_AI_RULES: settings.EMAIL_AI_RULES || "",
                },
                loadedFor: this.props.owner,
            });
        } catch (err) {
            this.props.sendAlert(err?.message || "Unable to load email settings.", "error");
        } finally {
            this.props.loading(false);
        }
    };

    saveSettings = async () => {
        if (!this.props.owner) return null;

        const result = await this.props.savePersonSettings({
            personUuid: this.props.owner,
            settings: this.state.settings,
        });

        this.setState({
            settings: result?.settings || this.state.settings,
            loadedFor: this.props.owner,
        });

        return result;
    };

    onChange = (e) => {
        const { name, value } = e.target;
        this.setState((prev) => ({
            settings: {
                ...(prev.settings || {}),
                [name]: value,
            },
        }));
    };

    onSignatureChange = (value) => {
        this.setState((prev) => ({
            settings: {
                ...(prev.settings || {}),
                EMAIL_SIGNATURE: value,
            },
        }));
    };

    onConfigurationChange = (option) => {
        this.setState((prev) => ({
            settings: {
                ...(prev.settings || {}),
                EMAIL_CONFIGURATION: option?.value ?? "",
            },
        }));
    };

    render() {
        const settings = this.state.settings || {};
        const configurationOptions = [
            { value: "", label: "System default" },
            ...(this.state.configurations || []).map((configuration) => ({
                value: configuration.name,
                label: configuration.name,
            })),
        ];
        const selectedConfiguration = configurationOptions.find(
            (option) => option.value === (settings.EMAIL_CONFIGURATION || "")
        ) || configurationOptions[0];

        return (
            <Col xs="12">
                <Row className="g-3">
                    <Col xs="12">
                        <h2 className="small-title">Sender</h2>
                    </Col>

                    <Col xs="12" lg="4">
                        <div className="mb-3 top-label">
                            <Select
                                inputId="EMAIL_CONFIGURATION"
                                name="EMAIL_CONFIGURATION"
                                classNamePrefix="react-select"
                                placeholder=""
                                options={configurationOptions}
                                value={selectedConfiguration}
                                onChange={this.onConfigurationChange}
                                isClearable={false}
                            />
                            <Form.Label htmlFor="EMAIL_CONFIGURATION">FROM</Form.Label>
                        </div>
                    </Col>

                    <Col xs="12" md="6" lg="4">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="EMAIL_DISPLAY"
                                value={settings.EMAIL_DISPLAY || ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>SENDER DISPLAY NAME</Form.Label>
                        </div>
                    </Col>

                    <Col xs="12" md="6" lg="4">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="email"
                                name="EMAIL_SENDER"
                                value={settings.EMAIL_SENDER || ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>SENDER EMAIL</Form.Label>
                        </div>
                    </Col>

                    <Col xs="12">
                        <h2 className="small-title mt-2">Rules</h2>
                    </Col>

                    <Col xs="12" lg="6">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="EMAIL_TO"
                                value={settings.EMAIL_TO || ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>TO RULE</Form.Label>
                        </div>
                    </Col>

                    <Col xs="12" md="6" lg="3">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="EMAIL_CC"
                                value={settings.EMAIL_CC || ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>CC RULE</Form.Label>
                        </div>
                    </Col>

                    <Col xs="12" md="6" lg="3">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="EMAIL_BCC"
                                value={settings.EMAIL_BCC || ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>BCC RULE</Form.Label>
                        </div>
                    </Col>

                    <Col xs="12">
                        <div className="mb-3 top-label">
                            <Form.Control
                                type="text"
                                name="EMAIL_SUBJECT"
                                value={settings.EMAIL_SUBJECT || ""}
                                onChange={this.onChange}
                            />
                            <Form.Label>SUBJECT</Form.Label>
                        </div>
                    </Col>

                    <Col xs="12">
                        <h2 className="small-title mt-2">Signature</h2>
                        <div className="mb-3">
                            <ReactQuill
                                className="profile-email-signature"
                                theme="snow"
                                value={settings.EMAIL_SIGNATURE || ""}
                                onChange={this.onSignatureChange}
                            />
                        </div>
                    </Col>

                    <Col xs="12">
                        <h2 className="small-title mt-2">AI Rules</h2>
                        <div className="mb-3">
                            <Form.Control
                                as="textarea"
                                rows={8}
                                name="EMAIL_AI_RULES"
                                value={settings.EMAIL_AI_RULES || ""}
                                onChange={this.onChange}
                            />
                            
                        </div>
                    </Col>
                </Row>
            </Col>
        );
    }
}

ProfileEmailSettings.propTypes = {
    auth: PropTypes.object.isRequired,
    owner: PropTypes.string,
    displayName: PropTypes.string,
    email: PropTypes.string,
    loading: PropTypes.func.isRequired,
    getConfigurations: PropTypes.func.isRequired,
    getPersonSettings: PropTypes.func.isRequired,
    savePersonSettings: PropTypes.func.isRequired,
    sendAlert: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps, {
    loading,
    getConfigurations,
    getPersonSettings,
    savePersonSettings,
    sendAlert,
}, null, { forwardRef: true })(ProfileEmailSettings);
